import { useState, useEffect, useCallback } from 'react';

export type TextScaleLevel = 'normal' | 'large' | 'extra-large';

interface TextScaleConfig {
  level: TextScaleLevel;
  multiplier: number;
  label: string;
  labelEs: string;
}

export const TEXT_SCALE_OPTIONS: TextScaleConfig[] = [
  { level: 'normal', multiplier: 1.2, label: 'Normal', labelEs: 'Normal' },
  { level: 'large', multiplier: 1.5, label: 'Large', labelEs: 'Grande' },
  { level: 'extra-large', multiplier: 2.0, label: 'Extra Large', labelEs: 'Extra grande' },
];

const STORAGE_KEY = 'mblearn-text-scale';

export function useTextScale() {
  const [scaleLevel, setScaleLevel] = useState<TextScaleLevel>('normal');

  // Load saved preference on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && TEXT_SCALE_OPTIONS.some(opt => opt.level === saved)) {
      setScaleLevel(saved as TextScaleLevel);
    }
  }, []);

  // Apply scale as CSS variable for study content only (not entire document)
  useEffect(() => {
    const config = TEXT_SCALE_OPTIONS.find(opt => opt.level === scaleLevel);
    if (config) {
      // Set CSS variable for study content scaling
      document.documentElement.style.setProperty('--study-text-scale', config.multiplier.toString());
      
      // Save preference
      localStorage.setItem(STORAGE_KEY, scaleLevel);
    }
  }, [scaleLevel]);

  const setScale = useCallback((level: TextScaleLevel) => {
    setScaleLevel(level);
  }, []);

  const getCurrentConfig = useCallback(() => {
    return TEXT_SCALE_OPTIONS.find(opt => opt.level === scaleLevel) || TEXT_SCALE_OPTIONS[0];
  }, [scaleLevel]);

  const getScaleMultiplier = useCallback(() => {
    const config = TEXT_SCALE_OPTIONS.find(opt => opt.level === scaleLevel);
    return config?.multiplier || 1.10;
  }, [scaleLevel]);

  return {
    scaleLevel,
    setScale,
    getCurrentConfig,
    getScaleMultiplier,
    options: TEXT_SCALE_OPTIONS,
  };
}
